import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { devtools } from 'zustand/middleware'
import type { SocialStream } from 'src/types/domain/SocialStream'
import { isDevtoolsEnabled } from './utils/isDevtoolsEnabled'

export type SocialStreamViewMode = 'video' | 'audio' | 'closed'
export type SocialStreamStatus = 'online' | 'offline'

interface SocialStreamState {
  gameId?: string
  socialStreams?: SocialStream[]
  viewMode: SocialStreamViewMode
  audioEnabled: boolean
  videoEnabled: boolean
  isMinimized: boolean
  lastAudioEnabled: boolean
  lastVideoEnabled: boolean
  showControls: boolean
  streamStatus: SocialStreamStatus
}

export interface SocialStreamActions {
  setSocialStreams: (socialStreams?: SocialStream[]) => void
  toggleAudio: () => void
  toggleVideo: () => void
  closeStream: () => void
  expandStream: () => void
  toggleMinimized: () => void
  setStreamStatus: (status: SocialStreamStatus) => void
  setShowControls: (value: boolean) => void
  setViewMode: (mode: SocialStreamViewMode) => void
  gameChanged: (gameId: string) => void
}

type SocialStreamStore = SocialStreamState & { actions: SocialStreamActions }

export const useSocialStreamStore = create<SocialStreamStore>()(
  devtools(
    immer((set) => ({
      viewMode: 'video',
      audioEnabled: true,
      videoEnabled: true,
      isMinimized: false,
      lastAudioEnabled: true,
      lastVideoEnabled: true,
      showControls: false,
      streamStatus: 'online',
      actions: {
        setSocialStreams: (socialStreams) => {
          set(
            (state) => {
              state.socialStreams = socialStreams
            }, 
            undefined, 
            'setSocialStreams'
          )
        },
        toggleAudio: () => {
          set(
            (state) => {
              const next = !state.audioEnabled
              state.audioEnabled = next
              if (!next && !state.videoEnabled) state.viewMode = 'closed'
              else if (next && !state.videoEnabled) state.viewMode = 'audio'
            }, 
            undefined, 
            'toggleAudio'
          )
        },
        toggleVideo: () => {
          set(
            (state) => {
              const next = !state.videoEnabled
              state.videoEnabled = next
              if (!next && state.audioEnabled) state.viewMode = 'audio'
              else if (!next && !state.audioEnabled) state.viewMode = 'closed'
              else state.viewMode = 'video'
            }, 
            undefined, 
            'toggleVideo'
          )
        },
        closeStream: () => {
          set(
            (state) => {
              state.lastAudioEnabled = state.audioEnabled
              state.lastVideoEnabled = state.videoEnabled
              state.audioEnabled = false
              state.videoEnabled = false
              state.viewMode = 'closed'
            }, 
            undefined, 
            'closeStream'
          )
        },
        expandStream: () => {
          set(
            (state) => {
              state.audioEnabled = state.lastAudioEnabled
              state.videoEnabled = state.lastVideoEnabled
              state.viewMode = state.lastVideoEnabled ? 'video' : 'audio'
            }, 
            undefined, 
            'expandStream'
          )
        },

        toggleMinimized: () => {
          set(
            (state) => {
              state.isMinimized = !state.isMinimized
            }, 
            undefined, 
            'toggleMinimized'
          )
        },
        setShowControls: (value) => {
          set(
            (state) => {
              state.showControls = value
           }, 
            undefined, 
            'setShowControls'
          )
        },
        setViewMode: (mode) => {
          set(
            (state) => {
              state.viewMode = mode
            }, 
            undefined, 
            'setViewMode'
          )
        },
        setStreamStatus: (streamStatus: SocialStreamStatus) => {
          set(
            (state) => {
              state.streamStatus = streamStatus
            },  
            undefined, 
            'setViewMode'
          )
        },
        gameChanged: (gameId: string) => {
          set(
            (state) => {
              state.gameId = gameId
              state.socialStreams = undefined
            },
            undefined,
            'gameChanged'
          )
        },
      }
    })),
    { name: 'SocialStreamStore', enabled: isDevtoolsEnabled() }
  )
)

export const useSocialStreamActions = () => useSocialStreamStore((state) => state.actions)

export const useSocialStreamViewMode = () => useSocialStreamStore((state) => state.viewMode)

export const useSocialStreamIsOffline = () => useSocialStreamStore((state) => state.streamStatus !== 'online')

export const useSocialStreamAudioEnabled = () => useSocialStreamStore((state) => state.audioEnabled)

export const useSocialStreamVideoEnabled = () =>  useSocialStreamStore((state) => state.videoEnabled)

export const useSocialStreamIsMinimized = () => useSocialStreamStore((state) => state.isMinimized)

export const useSocialStreamShowControls = () => useSocialStreamStore((state) => state.showControls)

export const useGetSocialStreams = (onlyLive = true) => {
  return useSocialStreamStore((state) => {
    const allStreams = state.socialStreams

    if (!allStreams) return undefined
    if (!onlyLive) return allStreams

    return Object.fromEntries(
      Object.entries(allStreams).filter(([, stream]) => stream.live)
    )
  })
}
